// React
import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'

// Components
import BOrderPicker from 'ui/blocks/Picker/types/order'
import BPicker from 'ui/blocks/Picker'
import InputField from 'ui/components/InputField'
import DateTimePicker from 'ui/components/Picker/DateTimePicker'

// Utils
import { getDateTimesForTimeslot } from 'ui/components/Picker/utils'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'
import { useTranslation } from 'shared/utils/useTranslation'
import { useFormikContext } from 'formik'

// Shared
import moment from 'shared/lib/moment'
import FormatUtils from 'shared/utils/format'
import { Field } from 'shared/form'

export const Timeslots = (props) => {
  const [selectedTimeslotId, setSelectedTimeslotId] = useState(undefined)

  const picker = usePicker()

  const form = useFormikContext()
  const { t } = useTranslation()

  const from = form.values.starts_at ? new Date(form.values.starts_at) : null
  const timeslots = props.timeslots.sortBy?.((timeslot) => {
    const key = Object.keys(timeslot.data)[0]

    return Number(timeslot.data[key].from.replace(':', ''))
  })

  const options =
    timeslots?.toArray?.().map((timeslot) => {
      const data = timeslot.data[Object.keys(timeslot.data)[0]]
      const from = FormatUtils.date(moment(data.from, 'HH:mm'), 'time')
      const till = FormatUtils.date(moment(data.till, 'HH:mm'), 'time')
      const label = `${from} - ${till}`
      const value = timeslot.id

      return { label, value }
    }) ?? []
  const availability = useMemo(() => {
    const availabilityData = props.availabilities
      .filter((availability) => moment(availability.date).isSame(form.values.starts_at, 'day'))
      .toList()

    if (!form.values.starts_at || !availabilityData) return []

    return timeslots?.toArray?.().map((timeslot) => {
      const data = timeslot.data[Object.keys(timeslot.data)[0]]

      const availabilityFromIndex = availabilityData.findIndex((el) => data.from === `${el.hour}:${el.minute}`)
      const availabilityTillIndex = availabilityData.findIndex((el) => data.till === `${el.hour}:${el.minute}`)

      const isTimeslotAvailable =
        availabilityData.slice(availabilityFromIndex, availabilityTillIndex).findIndex((el) => !el.available) === -1

      return {
        from: data.from,
        till: data.till,
        value: timeslot.id,
        available: isTimeslotAvailable,
        status: isTimeslotAvailable ? 'available' : 'unavailable',
        type: 'timeslot'
      }
    })
  }, [timeslots, props.availabilities, form.values])

  const hasError = !!(
    !!form.errors.starts_at?.date ||
    !!form.errors.starts_at?.time ||
    !!form.errors.stops_at?.date ||
    !!form.errors.stops_at?.time
  )

  const isDelivery = props.fulfillmentType === 'delivery'

  useEffect(() => {
    const getTimeslotFromDates = (startsAt, stopsAt) => {
      const from = FormatUtils.date(moment(startsAt), 'time')
      const till = FormatUtils.date(moment(stopsAt), 'time')
      const label = `${from} - ${till}`
      const timeslotOption = options.find((option) => option.label === label)

      if (!timeslotOption) {
        picker.onValidate(false)
      }

      return timeslotOption && props.timeslots.getById?.(timeslotOption.value)
    }

    const hasTimeslots = props.timeslots.size?.() > 0

    if (!selectedTimeslotId && hasTimeslots && form.values.starts_at) {
      const timeslot = getTimeslotFromDates(form.values.starts_at, form.values.stops_at)

      if (timeslot) setSelectedTimeslotId(timeslot.id)
    }
  })

  const handleChangeTimeslot = (timeslot, date) => {
    const [startsAt, stopsAt] = getDateTimesForTimeslot(date, timeslot)

    form.setValues({
      ...form.values,
      starts_at: startsAt.toJSON(),
      stops_at: stopsAt.toJSON()
    })

    setSelectedTimeslotId(timeslot.id)
  }

  const handleSelectTimeslot = (timeslotId) => {
    const timeslot = props.timeslots.getById(timeslotId)

    handleChangeTimeslot(timeslot, form.values.starts_at)
  }

  const handleMoveDay = (date) => {
    const nextDay = moment(date).add(1, 'days')

    handleChangeDate(nextDay, true)
  }

  const handleChangeDate = (date, move = false) => {
    const selectableTimeslots = picker.getSelectableTimeslots(date)
    const timeslot =
      selectableTimeslots.getById?.(selectedTimeslotId) || selectableTimeslots.first?.() || timeslots.first?.()

    // We need to set a value here to have access to it in the blur method
    form.setValues({
      ...form.values,
      starts_at: date
    })

    if (!timeslot && move) {
      handleMoveDay(date)
    }

    if (timeslot) handleChangeTimeslot(timeslot, date)

    picker.onChange('from')
  }

  const handleBlurDate = (date) => {
    if (!date) return

    if (!picker.isDaySelectable({ type: 'start', date })) {
      date = picker.setDefaultTime({ type: 'start', forceDefaultTime: true })

      handleChangeDate(date, true)
    }
  }

  return (
    <BOrderPicker.Timeslots disabled={props.disabled}>
      <BPicker.Field>
        <BPicker.Label>{t('common.date')}</BPicker.Label>
        <Field
          {...props}
          name="starts_at"
          onChange={handleChangeDate}
          onBlur={handleBlurDate}
          component={DateTimePicker}
          blockedDays={(date) => !picker.isDaySelectable({ type: 'start', date })}
          inputDateRef={picker.refs.fromDateRef}
          from={from}
          to={from}
          mode="from"
          data-tid="Starts at picker"
          onOpen={props.onOpen?.bind(null, 'start', false)}
          useTimes={false}
          onMonthChange={props.onMonthChange.bind(null, { type: 'start', values: form.values })}
          withAvailability
          hideErrorLabel
          initialError
          // Override default behavior from DayPicker and highlight only hovered day
          selection={({ hover }) => ({ from: hover, to: hover })}
          hideLabel
        />
      </BPicker.Field>
      <BPicker.Field>
        <BPicker.TimeslotLabelWrapper>
          <BPicker.Label>{isDelivery ? t('common.from') : t('common.start.present')}</BPicker.Label>
          <BPicker.Label>{isDelivery ? t('common.until') : t('common.stop.present')}</BPicker.Label>
        </BPicker.TimeslotLabelWrapper>
        <InputField
          {...props}
          field={{
            value: selectedTimeslotId,
            onChange: handleSelectTimeslot
          }}
          type="timeslots"
          options={options}
          inputRef={picker.refs.timeslotsRef}
          disabled={!form.values.starts_at}
          data-tid="Timeslots"
          onOpen={props.onOpen?.bind(null, { type: 'start' }, moment(form.values?.stops_at))}
          availabilities={availability}
          availabilityMode="timeslots"
          columns={1}
          error={hasError}
          hideErrorLabel
          initialError
          touched
        />
      </BPicker.Field>
    </BOrderPicker.Timeslots>
  )
}

Timeslots.propTypes = {
  fulfillmentType: PropTypes.oneOf(['pickup', 'delivery']),
  availabilities: PropTypes.object,
  timeslots: PropTypes.object,
  disabled: PropTypes.bool,
  onOpen: PropTypes.func,
  onMonthChange: PropTypes.func
}
